import Image from 'next/legacy/image';
import styled from 'styled-components';
import { Box, Button, Heading, Paragraph, Stack, SubtleText } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { colors } from '@99designs/emu/settings';
import { FeaturesList, FeaturesListProps, Price } from '@99designs/marketing-ui';
import { useTrackCTALinkClicked } from '../../../lib/events';
import type { ButtonColorConfig } from '@99designs/emu';
import type { HeroImage } from '../../../lib/productLandingGroups';
import type { VPProductLandingGroupKey } from '../../../lib/vpProductLandingGroups';
import type { Price as PriceType } from '../../../util/price';

const Small = styled(Box)`
    display: none;
    ${mediaQuery.only('small')} {
        display: block;
    }
`;

const Medium = styled(Box)`
    display: none;
    ${mediaQuery.only('medium')} {
        display: block;
    }
`;

const Normal = styled(Box)`
    display: none;
    ${mediaQuery.only('normal')} {
        display: block;
    }
`;

const Large = styled(Box)`
    display: none;
    ${mediaQuery.minimum('large')} {
        display: block;
    }
`;

export type HeroSectionProps = {
    categoryKey: VPProductLandingGroupKey;
    title: string;
    features: FeaturesListProps['features'];
    introduction: string;
    heroImage: HeroImage;
    heroCtaAppearance?: ButtonColorConfig;
    price: PriceType;
    ctaLink: string;
};

export const HeroSection = ({
    categoryKey,
    title,
    features,
    price,
    introduction,
    heroImage,
    heroCtaAppearance,
    ctaLink,
}: HeroSectionProps) => {
    const primaryCTARefHero = useTrackCTALinkClicked({
        category: categoryKey,
        ctaLocation: 'hero section',
        ctaButton: 'primary',
    });

    return (
        <Box
            display="flex"
            flexDirection={['column', 'row']}
            background={`linear-gradient(${colors.white}, ${colors.grey100})`}
        >
            <Box
                width={['inherit', '50%']}
                display="flex"
                justifyContent={['flex-start', 'flex-end']}
            >
                <Box
                    maxWidth="650px"
                    width="100%"
                    paddingLeft={['1bu', '2bu']}
                    paddingRight={['1bu', '3bu']}
                    paddingBottom={['2bu', '4bu']}
                >
                    <Box paddingTop={['3bu', '4bu']} paddingBottom="1bu">
                        <Heading accessibilityLevel={1} appearanceLevel={0}>
                            {title}
                        </Heading>
                    </Box>
                    <Paragraph>{introduction}</Paragraph>
                    <Box paddingTop="1bu">
                        <Stack space="0.5bu">
                            <Heading accessibilityLevel={2} appearanceLevel={5}>
                                {__('What you get')}
                            </Heading>
                            <FeaturesList features={features} />
                        </Stack>
                        <Box paddingTop="1bu">
                            <SubtleText>Printing and logo design costs are additional.</SubtleText>
                        </Box>
                    </Box>
                    {price && (
                        <Box paddingTop="1bu">
                            <Heading accessibilityLevel={2} appearanceLevel={4}>
                                <Price amount={price.amountInCents} currency={price.currency} />
                            </Heading>
                        </Box>
                    )}
                    <Box
                        paddingTop={['1.5bu', '2bu']}
                        display="flex"
                        flexDirection={['column', 'row', 'row', 'row']}
                    >
                        <Box marginRight={['none', '2bu', '2bu', '2bu']}>
                            <Button
                                appearance={heroCtaAppearance}
                                href={ctaLink}
                                size="large"
                                type="button"
                                width={['fill', 'auto', 'auto', 'auto']}
                                ref={primaryCTARefHero}
                            >
                                See how it works
                            </Button>
                        </Box>
                        <Box
                            display="flex"
                            alignItems="center"
                            paddingTop={['1bu', '0.75bu', '0.75bu', '0.75bu']}
                            paddingBottom={['0.5bu', '1bu', '1bu', '1bu']}
                        >
                            <Paragraph weight="bold" color={colors.grey800} size="small">
                                Crafted with:
                            </Paragraph>{' '}
                            <Box>
                                <img
                                    src="https://assets.99static.com/create/vistaprint-logo-text.svg"
                                    alt="Vistaprint logo"
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Small
                width={['inherit', '50%']}
                minHeight={['600px', 'inherit']}
                maxWidth="1000px"
                position="relative"
            >
                <Image
                    src={heroImage.sources[0]}
                    alt={heroImage.altText}
                    objectFit="cover"
                    objectPosition="top"
                    layout="fill"
                    sizes="100vw"
                    quality={75}
                    priority
                    role="presentation"
                />
            </Small>
            <Medium
                width={['inherit', '50%']}
                minHeight={['600px', 'inherit']}
                maxWidth="1000px"
                position="relative"
            >
                <Image
                    src={heroImage.sources[1]}
                    alt={heroImage.altText}
                    objectFit="cover"
                    objectPosition="top"
                    layout="fill"
                    sizes="100vw"
                    quality={75}
                    priority
                    role="presentation"
                />
            </Medium>
            <Normal
                width={['inherit', '50%']}
                minHeight={['600px', 'inherit']}
                maxWidth="1000px"
                position="relative"
            >
                <Image
                    src={heroImage.sources[2]}
                    alt={heroImage.altText}
                    objectFit="cover"
                    objectPosition="left"
                    layout="fill"
                    sizes="50vw"
                    quality={75}
                    priority
                    role="presentation"
                />
            </Normal>
            <Large
                width={['inherit', '50%']}
                minHeight={['600px', 'inherit']}
                maxWidth="1000px"
                position="relative"
            >
                <Image
                    src={heroImage.sources[3]}
                    alt={heroImage.altText}
                    objectFit="cover"
                    objectPosition="left"
                    layout="fill"
                    sizes="50vw"
                    quality={75}
                    priority
                    role="presentation"
                />
            </Large>
        </Box>
    );
};
